import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Modal,
  Fade,
  Popover,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  format,
  addDays,
  eachDayOfInterval,
  subDays,
  isSameDay,
  parse,
} from "date-fns";
import { ArrowDropUp, ArrowDropDown, Info } from "@mui/icons-material";
import IconConstants from "../../utils/IconConstants";
import { END_POINT } from "../../utils/End-points";
import ApiClient from "../../apis/Api-client";
import { Helper } from "../../utils/Helpers";
import {
  OCCUPANCY_STATUSES,
  RESERVATION_STATUSES,
} from "../../utils/Constants";
import { useCanFn, FEATURES, ACTIONS } from "../../core/permissions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { setDashBoardData } from "../../core/store/redux/slices/Dashboard-slice";
import RoomShift from "../../components/roomshift/Room-shift";
import ExtendStay from "../../components/extendstay/Extend-stay";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CancelReservationModal from "../reservation/CancelReservationModal";
import AssignRoomModal from "../reservation/AssignRoomModal";
import {
  selectCalendarData,
  setCalendarData,
} from "../../core/store/redux/slices/Calendar-slice";
import { debounce } from "lodash";
import { CustomTooltip } from "../reports/ColumnUtils";
import { trackPromise } from "react-promise-tracker";
const { getLocalTimeFromUtc, getGradientTextStyle } = new Helper();
const { CHECK_IN } = OCCUPANCY_STATUSES;
const { CONFIRMED, TENTATIVE } = RESERVATION_STATUSES;

const houseKeepingIcons = {
  clean: IconConstants.CleanedCal,
  assigned: IconConstants.AssignedCal,
  dirty: IconConstants.DirtyCal,
};

const AVAILABILITY_COLORS = {
  vacant: "#2E7D32",
  reserved: "#9747FF",
  occupied: "#ED705B",
  blocked: "#9E9E9E",
};

const AVAILABILITY_ICONS = {
  vacant: IconConstants.VacantIcon,
  reserved: IconConstants.AllocatedViolet,
  occupied: IconConstants.OccupiedIcon,
  blocked: IconConstants.BlockedIcon,
};

const WeekView = ({
  selectedDate,
  roomTypeData,
  isExpanded,
  dayClicked,
  setDayClicked,
  isEnglish,
  statusFilter = "combined",
}) => {
  const { api } = ApiClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const can = useCanFn();
  const { palette } = useTheme();
  const params = new URLSearchParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [assignRoomAnchorEl, setAssignRoomAnchorEl] = useState(null);
  const openpop = Boolean(anchorEl);
  const id = openpop ? "calendar-popover" : undefined;
  const isTab = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isMob = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const bookingData = useSelector(selectCalendarData);
  const [startDate, setStartDate] = useState(selectedDate);
  const [events, setEvents] = useState([]);
  const [expandedRoomTypes, setExpandedRoomTypes] = useState({});
  const [modalContent, setModalContent] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [activeRowData, setActiveRowData] = useState({});
  const [daysOfMonths, setDaysOfMonths] = useState([]);
  const dayRefs = useRef([]);
  const barRefs = useRef({});
  const tableContainerRef = useRef(null);
  const [bookingId, setBookingId] = useState("");
  const isUpdating = useRef(false);
  const normCache = useRef(new Map());
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    const container = tableContainerRef.current;
    let startX, startY, isScrollingHorizontally, isScrollingVertically;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrollingHorizontally = false;
      isScrollingVertically = false;
    };

    const handleTouchMove = (e) => {
      const moveX = e.touches[0].clientX - startX;
      const moveY = e.touches[0].clientY - startY;

      if (!isScrollingHorizontally && !isScrollingVertically) {
        if (Math.abs(moveX) > Math.abs(moveY)) {
          isScrollingHorizontally = true;
        } else {
          isScrollingVertically = true;
        }
      }

      if (isScrollingHorizontally) {
        e.preventDefault(); // Prevent vertical scrolling when moving horizontally
        container.scrollLeft -= moveX * 0.1;
      }

      if (isScrollingVertically) {
        e.preventDefault(); // Prevent horizontal scrolling when moving vertically
        container.scrollTop -= moveY * 0.1;
      }
    };

    if (container) {
      container.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      container.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    }

    return () => {
      if (container) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, []);

  const addNoRoomReservation = (roomTypeData) => {
    return roomTypeData.map((roomType) => {
      const noRoom = {
        room_id: roomType.room_type_key + roomType.room_type_id,
        room_no: "",
      };
      const updatedRooms = [...roomType.rooms, noRoom];
      return {
        ...roomType,
        rooms: updatedRooms,
      };
    });
  };

  const updatedRoomTypeData = addNoRoomReservation(roomTypeData);

  const handleClosePopover = () => {
    setAssignRoomAnchorEl(null);
    setOpenModal(false);
    setAnchorEl(null);
    setTimeout(() => {
      setActiveRowData({});
    });
  };

  const handleTooltipToggle = (roomId) => {
    setActiveTooltip((prevId) => (prevId === roomId ? null : roomId));
  };

  const handleTooltipClose = () => {
    setActiveTooltip(null);
  };

  const startOfPrevMonth = useMemo(() => {
    return daysOfMonths.length > 0 ? daysOfMonths[0] : null;
  }, [daysOfMonths]);

  const endOfNextMonth = useMemo(() => {
    return daysOfMonths.length > 0
      ? daysOfMonths[daysOfMonths.length - 1]
      : null;
  }, [daysOfMonths]);

  useEffect(() => {
    if (startOfPrevMonth) {
      const debounceFetch = setTimeout(() => {
        getAllBookings();
      }, 100);

      return () => clearTimeout(debounceFetch);
    }
  }, [startOfPrevMonth, endOfNextMonth]);

  const getAllBookings = async () => {
    const from_date = moment(new Date(startOfPrevMonth))
      .subtract(10, "days")
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");

    const to_date = moment(new Date(endOfNextMonth))
      .add(10, "days")
      .endOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    try {
      const response = await api.post(END_POINT.BOOKING.GET_ALL_BOOKING, {
        from_date,
        to_date,
      });
      if (response.status === 200) {
        const { data } = response?.data;
        if (data?.length) {
          const localDates = new Map();
          const getLocalDate = (utcDate) => {
            if (!utcDate) return null;
            if (localDates.has(utcDate)) return localDates.get(utcDate);
            const result = getLocalTimeFromUtc(utcDate)?.dateString;
            localDates.set(utcDate, result);
            return result;
          };

          const enriched = data.map((v) => ({
            ...v,
            _checkin: getLocalDate(v.checkin_date),
            _checkout: getLocalDate(v.checkout_date),
            _groupKey: v.room_id || v.room_type_key,
          }));

          const groupIndex = new Map();
          for (const b of enriched) {
            const key = b._groupKey;
            if (!groupIndex.has(key)) groupIndex.set(key, []);
            groupIndex.get(key).push(b);
          }

          const updatedData = enriched.map((v) => {
            const group = groupIndex.get(v._groupKey) || [];

            let maxCheckoutDate = v._checkout;
            const queue = [v];
            const visited = new Set();
            visited.add(v.reservation_id);

            while (queue.length) {
              const current = queue.shift();
              for (const other of group) {
                if (
                  current._checkin <= other._checkout &&
                  current._checkout >= other._checkin &&
                  !visited.has(other.reservation_id)
                ) {
                  visited.add(other.reservation_id);
                  queue.push(other);
                  if (other._checkout > maxCheckoutDate) {
                    maxCheckoutDate = other._checkout;
                  }
                }
              }
            }

            return {
              ...v,
              roomId: v.room_id || null,
              checkin: v._checkin,
              checkout: v._checkout,
              status: v.booking_status,
              customerName: v.name,
              fake_checkout: maxCheckoutDate,
            };
          });

          setEvents(updatedData);
        }
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const toggleExpand = useCallback(
    debounce((roomTypeKey) => {
      trackPromise(
        new Promise((resolve) => {
          setTimeout(() => {
            setExpandedRoomTypes((prev) => ({
              ...prev,
              [roomTypeKey]: !prev[roomTypeKey],
            }));
            resolve("Expanded");
          }, 10);
        })
      );
    }, 5),
    []
  );

  useEffect(() => {
    if (roomTypeData?.length > 0 && !bookingData) {
      setExpandedRoomTypes({
        [roomTypeData[0].room_type_name]: true,
      });
    }
  }, [roomTypeData]);

  useEffect(() => {
    if (bookingData && events.length > 0) {
      const { checkin_date, room_type_name, booking_id } = bookingData ?? {};
      setStartDate(checkin_date);
      setBookingId(booking_id);
      setExpandedRoomTypes({
        [room_type_name]: true,
      });
      const booking = events.find((event) => event.booking_id === booking_id);
      if (booking) {
        const checkInDate = new Date(booking.checkin);
        const columnIndex = daysOfMonths.findIndex((day) =>
          isSameDay(new Date(day), checkInDate)
        );
        if (columnIndex !== -1 && dayRefs.current[columnIndex]) {
          dayRefs.current[columnIndex].scrollIntoView({
            behavior: "smooth",
            inline: "center",
          });

          const barRef = barRefs.current[booking_id];
          if (barRef) {
            barRef.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "center",
            });
            dispatch(setCalendarData());
            window.scrollTo(0, 0);
          }
        }
      }
    }
  }, [events, bookingData]);

  const normalizeDate = (date) => {
    const key = date instanceof Date ? date.getTime() : date;
    if (normCache.current.has(key)) return normCache.current.get(key);
    const result = moment(date).startOf("day").toDate();
    normCache.current.set(key, result);
    return result;
  };

  const eventIndex = useMemo(() => {
    const byKey = new Map();
    const unassignedByType = new Map();
    for (const b of events) {
      const ciTs = normalizeDate(b.checkin).getTime();
      const coTs = normalizeDate(b.checkout).getTime();
      const fcoTs = normalizeDate(b.fake_checkout || b.checkout).getTime();
      const entry = { ...b, _ciTs: ciTs, _coTs: coTs, _fcoTs: fcoTs };
      if (b.roomId != null) {
        if (!byKey.has(b.roomId)) byKey.set(b.roomId, []);
        byKey.get(b.roomId).push(entry);
      } else {
        const ck = (b.room_type_key || "") + (b.room_type_id || "");
        if (!byKey.has(ck)) byKey.set(ck, []);
        byKey.get(ck).push(entry);
        if (b.room_type_key) {
          if (!unassignedByType.has(b.room_type_key)) unassignedByType.set(b.room_type_key, []);
          unassignedByType.get(b.room_type_key).push(entry);
        }
      }
    }
    return { byKey, unassignedByType };
  }, [events]);

  const getCellStatus = (roomId, date, weekStart, weekEnd) => {
    const norm = normalizeDate(date).getTime();
    const candidates = eventIndex.byKey.get(roomId) || [];
    const bookingsForDate = candidates.filter((b) => norm >= b._ciTs && norm <= b._fcoTs);
    bookingsForDate.sort((a, b) => a._ciTs - b._ciTs);
    const wStart = normalizeDate(weekStart).getTime();
    const wEnd = normalizeDate(weekEnd).getTime();
    return bookingsForDate.map((booking) => {
      const isFirstVisibleCell =
        booking._ciTs >= wStart && booking._ciTs <= wEnd
          ? norm === booking._ciTs
          : norm === wStart;
      return { ...booking, isFirstVisibleCell };
    });
  };

  const getRoomTypeCounts = useCallback(
    (group, date) => {
      const realRooms = (group?.rooms || []).filter((r) => r.room_no);
      const total = realRooms.length;
      const norm = normalizeDate(date).getTime();
      let occupied = 0;
      let reserved = 0;
      let blocked = 0;

      realRooms.forEach((room) => {
        const roomEvents = eventIndex.byKey.get(room.room_id) || [];
        const roomBookings = roomEvents.filter((b) => norm >= b._ciTs && norm <= b._coTs);
        const hasOccupied = roomBookings.some((b) => b.status === CHECK_IN);
        const hasReserved = roomBookings.some((b) =>
          [CONFIRMED, TENTATIVE].includes(b.status)
        );

        let isBlocked = false;
        if (room.is_under_maintenance) {
          const mStart = getLocalTimeFromUtc(room.maintenance_start)?.dateString;
          const mUntil = getLocalTimeFromUtc(room.maintenance_until)?.dateString;
          if (mStart && mUntil) {
            isBlocked =
              norm >= normalizeDate(mStart).getTime() &&
              norm <= normalizeDate(mUntil).getTime();
          }
        }

        if (hasOccupied) occupied += 1;
        else if (hasReserved) reserved += 1;
        else if (isBlocked) blocked += 1;
      });

      const unassignedEvents = eventIndex.unassignedByType.get(group.room_type_key) || [];
      const unassignedReserved = unassignedEvents.filter((b) => {
        if (![CONFIRMED, TENTATIVE].includes(b.status)) return false;
        return norm >= b._ciTs && norm <= b._coTs;
      }).length;
      reserved += unassignedReserved;

      // AC-12: Available = Total - Reserved - Occupied - Blocked.
      const vacant = Math.max(0, total - occupied - reserved - blocked);
      return { total, occupied, reserved, blocked, vacant };
    },
    [eventIndex]
  );

  const isRoomVacantOnDate = useCallback(
    (room, date) => {
      if (!room?.room_no) return 0;
      const norm = normalizeDate(date).getTime();
      if (room.is_under_maintenance) {
        const mStart = getLocalTimeFromUtc(room.maintenance_start)?.dateString;
        const mUntil = getLocalTimeFromUtc(room.maintenance_until)?.dateString;
        if (
          mStart &&
          mUntil &&
          norm >= normalizeDate(mStart).getTime() &&
          norm <= normalizeDate(mUntil).getTime()
        ) {
          return 0;
        }
      }
      const roomEvents = eventIndex.byKey.get(room.room_id) || [];
      const booked = roomEvents.some((b) => {
        return (
          norm >= b._ciTs &&
          norm <= b._coTs &&
          (b.status === CHECK_IN || [CONFIRMED, TENTATIVE].includes(b.status))
        );
      });
      return booked ? 0 : 1;
    },
    [eventIndex]
  );

  const countsMap = useMemo(() => {
    const map = {};
    if (!startOfPrevMonth) return map;
    (roomTypeData || []).forEach((group) => {
      daysOfMonths.forEach((day, index) => {
        const date = addDays(new Date(startOfPrevMonth), index);
        map[`${group.room_type_key}_${index}`] = getRoomTypeCounts(group, date);
      });
    });
    return map;
  }, [roomTypeData, daysOfMonths, startOfPrevMonth, getRoomTypeCounts]);

  const renderBigCell = (value, color, iconSrc) => {
    if (!value) return null;
    return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: "12vw", sm: "6vw", md: "3.6vw" },
      }}
    >
      <Box
        component="img"
        src={iconSrc}
        alt=""
        sx={{
          width: { xs: "8.4vw", sm: "3.95vw", md: "2.28vw" },
          height: { xs: "8.4vw", sm: "3.95vw", md: "2.28vw" },
        }}
      />
      <Typography
        sx={{
          position: "absolute",
          bottom: { xs: "0.6vw", sm: "0.3vw", md: "0.2vw" },
          right: { xs: "1.8vw", sm: "1vw", md: "0.6vw" },
          fontSize: { xs: "3vw", sm: "1.7vw", md: "1vw" },
          fontWeight: 700,
          color,
          lineHeight: 1,
        }}
      >
        {value ?? 0}
      </Typography>
    </Box>
    );
  };

  const renderCountsCell = (counts) => {
    const c = counts || { vacant: 0, reserved: 0, occupied: 0, blocked: 0 };
    if (statusFilter === "vacant")
      return renderBigCell(
        c.vacant,
        AVAILABILITY_COLORS.vacant,
        AVAILABILITY_ICONS.vacant
      );
    if (statusFilter === "reservation")
      return renderBigCell(
        c.reserved,
        AVAILABILITY_COLORS.reserved,
        AVAILABILITY_ICONS.reserved
      );
    if (statusFilter === "occupied")
      return renderBigCell(
        c.occupied,
        AVAILABILITY_COLORS.occupied,
        AVAILABILITY_ICONS.occupied
      );
    if (statusFilter === "blocked")
      return renderBigCell(
        c.blocked,
        AVAILABILITY_COLORS.blocked,
        AVAILABILITY_ICONS.blocked
      );
    const segments = [
      { value: c.vacant, color: AVAILABILITY_COLORS.vacant },
      { value: c.reserved, color: AVAILABILITY_COLORS.reserved },
      { value: c.occupied, color: AVAILABILITY_COLORS.occupied },
      { value: c.blocked, color: AVAILABILITY_COLORS.blocked },
    ].filter((s) => s.value > 0);

    if (!segments.length) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: "1.1vw", sm: "0.6vw", md: "0.34vw" },
          justifyContent: "center",
          alignItems: "center",
          maxWidth: { xs: "20vw", sm: "9.6vw", md: "5.5vw" },
          mx: "auto",
          py: { xs: "1.2vw", sm: "0.6vw", md: "0.35vw" },
        }}
      >
        {segments.map((s, i) => (
          <Box
            key={i}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: { xs: "6vw", sm: "3vw", md: "1.75vw" },
              px: { xs: "1.4vw", sm: "0.7vw", md: "0.42vw" },
              py: { xs: "0.5vw", sm: "0.25vw", md: "0.14vw" },
              borderRadius: "6px",
              backgroundColor: `${s.color}1A`,
              border: `0.5px solid ${s.color}40`,
              color: s.color,
              fontWeight: 700,
              fontSize: { xs: "2.5vw", sm: "1.45vw", md: "0.82vw" },
              lineHeight: 1.3,
            }}
          >
            {s.value}
          </Box>
        ))}
      </Box>
    );
  };

  const getEventBarStyle = ({
    status,
    reservation_key,
    booking_id,
    isRealCheckout,
  }) => {
    const isActive =
      activeRowData?.reservation_key === reservation_key ||
      booking_id === bookingId;
    return {
      backgroundColor: (() => {
        if (isRealCheckout) {
          if (status === CHECK_IN) {
            return isActive ? "#ED705B" : "#FFE7E4";
          }
          return isActive ? "#9747FF" : "#F3EAFF";
        }
        return "#FFFFFF";
      })(),
      color: isActive ? "#FFFFFF" : status === CHECK_IN ? "#ED705B" : "#9747FF",
      width: isRealCheckout ? "calc(100% + 2px)" : "100%",
      marginLeft: isRealCheckout ? "-1px" : 0,
      marginRight: isRealCheckout ? "-1px" : 0,
      zIndex: 1,
      height: { xs: "5.972vw", sm: "3.597vw", md: "2.084vw" },
      display: "flex",
      alignItems: "center",
      mt: { xs: "1vw", sm: "0.719vw", md: "0.417vw" },
      pl: "4%",
      borderTop: (() => {
        if (isRealCheckout) {
          if (status === CHECK_IN) {
            return `0.139vw solid ${isActive ? "#FFE7E4" : "#ED705B"}`;
          }
          return `0.139vw solid ${isActive ? "#F3EAFF" : "#9747FF"}`;
        }
        return "none";
      })(),
      cursor: isRealCheckout ? "pointer" : "default",
    };
  };

  const getBlockedBarStyle = (isOriginalBlockedRange) => {
    return {
      backgroundColor: isOriginalBlockedRange ? "#EFEFEF" : "transparent",
      color: "#000000",
      width: "100%",
      width: "calc(100% + 2px)",
      marginLeft: "-1px",
      marginRight: "-1px",
      height: { xs: "5.972vw", sm: "3.597vw", md: "2.084vw" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mt: { xs: "1vw", sm: "0.719vw", md: "0.417vw" },
      pl: "4%",
      borderTop: isOriginalBlockedRange ? "0.139vw solid #C7C7C7" : "none",
      cursor: "default",
    };
  };

  const optionsData = [
    {
      label: "Check In",
      icon: IconConstants.CalendarCheckIn,
      permFeature: FEATURES.CHECK_IN,
      permAction: ACTIONS.CREATE,
      action: () => {
        setTimeout(() => {
          params.append("reservation_key", activeRowData?.reservation_key);
          navigate(`/main/arrival/reservedcheckin?${params.toString()}`);
        });
      },
    },
    {
      label: "Assign Room",
      icon: IconConstants.CalendarAssignRoom,
      permFeature: FEATURES.RESERVATIONS,
      permAction: ACTIONS.UPDATE,
      action: () => {
        setAssignRoomAnchorEl(activeRowData?.currentTarget);
      },
    },
    {
      label: "Check Out",
      icon: IconConstants.CalendarCheckOutOption,
      permFeature: FEATURES.CHECK_OUT,
      permAction: ACTIONS.CREATE,
      action: () => {
        setTimeout(() => {
          params.append(
            "occupancy_keys",
            JSON.stringify([
              activeRowData.parent_occupancy_key ?? activeRowData.occupancy_key,
            ])
          );
          navigate(`/main/departure?${params.toString()}`);
        });
      },
    },
    {
      label: "Guest Details",
      icon: IconConstants.GuestDetailsGray,
      permFeature: FEATURES.GUEST_MANAGEMENT,
      permAction: ACTIONS.VIEW,
      action: () => {
        const { parent_occupancy_key, reservation_key, occupancy_key } =
          activeRowData;
        dispatch(
          setDashBoardData({
            occupancy_key: parent_occupancy_key ?? occupancy_key,
            reservation_key,
          })
        );
        setTimeout(() => {
          navigate(
            `/main/${activeRowData?.booking_status === CHECK_IN
              ? "guestinfo"
              : "reservations"
            }`
          );
        });
      },
    },
    {
      label: "Room Shift",
      icon: IconConstants.RoomShiftGray,
      permFeature: FEATURES.RESERVATIONS,
      permAction: ACTIONS.ROOM_SHIFT,
      action: () => {
        setModalContent("roomShift");
        setOpenModal(true);
      },
    },
    {
      label: "Extend Stay",
      icon: IconConstants.ExtendStayGray,
      permFeature: FEATURES.RESERVATIONS,
      permAction: ACTIONS.EXTEND_STAY,
      action: () => {
        setModalContent("extendStay");
        setOpenModal(true);
      },
    },
    {
      label: "Cancel Reservation",
      icon: IconConstants.CalendarCancel,
      permFeature: FEATURES.RESERVATIONS,
      permAction: ACTIONS.CANCEL_RESERVATION,
      activeIcon: IconConstants.CancelWhite,
      action: () => {
        setModalContent("cancelReservation");
        setOpenModal(true);
      },
    },
  ];

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleStatusBarClick = (event, data) => {
    setBookingId("");
    setAnchorEl(event.currentTarget);
    setActiveRowData({ ...data, currentTarget: event.currentTarget });
  };

  const handleCellClick = (data) => {
    const { room_key, selectedDate } = data;
    const isToday = selectedDate === new Date().setHours(0, 0, 0, 0);
    params.append("checkin_date", format(new Date(selectedDate), "yyyy-MM-dd"));
    if (isToday) {
      params.append("room_key", room_key);
      navigate(`/main/arrival?${params.toString()}`);
    } else {
      params.append("room_key", room_key);
      navigate(`/main/reservation?${params.toString()}`);
    }
  };

  const parseDate = (dateString) => {
    return parse(dateString, "EEEE dd MMMM yy", new Date());
  };

  const formatDate = (date) => {
    return format(date, "EEEE dd MMMM yy");
  };

  // Initial load: 15 days before and after startDate (total 31 days)
  useEffect(() => {
    const initialStart = subDays(startDate, 15);
    const initialEnd = addDays(startDate, 15);
    const initialDays = eachDayOfInterval({
      start: initialStart,
      end: initialEnd,
    }).map((date) => formatDate(date));
    setDaysOfMonths(initialDays);
  }, [startDate]);

  // Add next 10 days
  const addNextDays = useCallback(() => {
    if (isUpdating.current) return;
    isUpdating.current = true;

    const lastDay = parseDate(daysOfMonths[daysOfMonths.length - 1]);
    const newEnd = addDays(lastDay, 10);
    const newDays = eachDayOfInterval({
      start: addDays(lastDay, 1),
      end: newEnd,
    }).map((date) => formatDate(date));

    setDaysOfMonths((prevDays) => {
      const existingDays = new Set(prevDays);
      const uniqueNewDays = newDays.filter((day) => !existingDays.has(day));
      isUpdating.current = false;
      return [...prevDays, ...uniqueNewDays];
    });
  }, [daysOfMonths]);

  //Add previous 10 days
  const addPreviousDays = useCallback(() => {
    if (isUpdating.current) return;
    isUpdating.current = true;

    const firstDay = parseDate(daysOfMonths[0]);
    const newStart = subDays(firstDay, 10);
    const newDays = eachDayOfInterval({
      start: newStart,
      end: subDays(firstDay, 1),
    }).map((date) => formatDate(date));

    setDaysOfMonths((prevDays) => {
      const existingDays = new Set(prevDays);
      const uniqueNewDays = newDays.filter((day) => !existingDays.has(day));
      isUpdating.current = false;
      return [...uniqueNewDays, ...prevDays];
    });
  }, [daysOfMonths]);

  //Remove oldest 10 days
  const removeOldestDays = useCallback(() => {
    setDaysOfMonths((prevDays) => prevDays.slice(10));
  }, []);

  //Remove newest 10 days
  const removeNewestDays = useCallback(() => {
    setDaysOfMonths((prevDays) => prevDays.slice(0, -10));
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let lastScrollLeft = container.scrollLeft;
    let lastScrollTop = container.scrollTop;

    const handleScroll = debounce(() => {
      const { scrollLeft, scrollTop, scrollWidth, clientWidth } = container;

      // Check if the scroll is horizontal (scrollLeft has changed)
      const isHorizontalScroll = scrollLeft !== lastScrollLeft;

      // Check if the scroll is vertical (scrollTop has changed)
      const isVerticalScroll = scrollTop !== lastScrollTop;

      // Update last scroll positions
      lastScrollLeft = scrollLeft;
      lastScrollTop = scrollTop;

      if (isVerticalScroll) return;

      if (isHorizontalScroll) {
        const threshold = 12;
        if (Math.ceil(scrollLeft + clientWidth) >= scrollWidth - threshold) {
          addNextDays();
          removeOldestDays();
          setTimeout(() => {
            container.scrollLeft =
              container.scrollWidth / (isMob ? 1.5 : isTab ? 1.6 : 1.8) -
              container.clientWidth / (isMob ? 1.5 : isTab ? 1.6 : 1.8);
          }, 0);
        }

        if (scrollLeft <= threshold) {
          addPreviousDays();
          removeNewestDays();
          setTimeout(() => {
            container.scrollLeft =
              container.scrollWidth / (isMob ? 3.1 : isTab ? 2.7 : 2.2) -
              container.clientWidth / (isMob ? 3.1 : isTab ? 2.7 : 2.2);
          }, 0);
        }
      }
    }, 30);

    container.addEventListener("scroll", handleScroll);
    return () => {
      handleScroll.cancel();
      container.removeEventListener("scroll", handleScroll);
    };
  }, [addNextDays, addPreviousDays, removeOldestDays, removeNewestDays]);

  // Scroll to the startDate column on initial render
  useEffect(() => {
    if (daysOfMonths.length > 0 && tableContainerRef.current && dayClicked) {
      const startDateIndex = daysOfMonths.findIndex((day) =>
        isSameDay(new Date(day), startDate)
      );
      if (startDateIndex !== -1 && dayRefs.current[startDateIndex]) {
        dayRefs.current[startDateIndex].scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest",
        });
        // window.scrollTo(0, 0);
        setDayClicked(false);
      }
    }
  }, [daysOfMonths, startDate]);

  return (
    <>
      <TableContainer
        sx={{
          maxHeight: isExpanded
            ? { xs: "68vh", sm: "98vw", md: "76vh" }
            : { xs: "34.5vh", sm: "51vw", md: "55vh" },
          border: "1px solid #FFDC8A",
          borderRadius: "12px",
          WebkitOverflowScrolling: "touch",
          touchAction: "none",
        }}
        id="scrollable-container"
        ref={tableContainerRef}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: {
              xs: "900px",
              sm: "1200px",
              md: "1500px",
              "& .MuiTableCell-root": {
                padding: "0 !important",
              },
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ height: { md: "4.583vw" } }}>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  background: "#FFFFFF",
                  border: "0.5px solid #FCF0D4",
                  p: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "stretch" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: "14.149vw", sm: "14.03vw", md: "8.194vw" },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "5.129vw", sm: "3.837vw", md: "2.223vw" },
                        height: { xs: "5.129vw", sm: "3.837vw", md: "2.223vw" },
                      }}
                    >
                      <img
                        src={IconConstants.RoomsCal}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: "2.565vw",
                          sm: "1.919vw",
                          md: "1.112vw",
                        },
                        ...getGradientTextStyle(palette),
                        fontWeight: 600,
                      }}
                    >
                      {t("pages.home_page.room_view_section.rooms")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: "13vw", sm: "8vw", md: "6vw" },
                      borderLeft: "0.5px solid #FCF0D4",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "2.4vw", sm: "1.6vw", md: "0.92vw" },
                        ...getGradientTextStyle(palette),
                        fontWeight: 600,
                      }}
                    >
                      {t("pages.home_page.room_view_section.available")}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              {daysOfMonths.map((weekDay, index) => {
                const [day, date, month, year] = weekDay.split(" ");
                return (
                  <TableCell
                    key={weekDay}
                    align="center"
                    ref={(el) => (dayRefs.current[index] = el)}
                    sx={{
                      border: "0.5px solid #FCF0D4",
                    }}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: "21.026vw",
                          sm: "9.85vw",
                          md: "5.694vw",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "3.077vw",
                            sm: "2.399vw",
                            md: "1.389vw",
                          },
                          fontWeight: 700,
                          ...getGradientTextStyle(palette)
                        }}
                      >
                        {date}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "2.565vw",
                            sm: "1.439vw",
                            md: "0.834vw",
                          },
                          color: palette.text.primary,
                          textWrap: "nowrap",
                        }}
                      >
                        {isEnglish
                          ? `${t(`days.${day.toLowerCase()}`).slice(
                            0,
                            3
                          )} | ${t(`months.${month.toLowerCase()}`).slice(
                            0,
                            3
                          )}`
                          : `${t(`days.${day.toLowerCase()}`)} | ${t(
                            `months.${month.toLowerCase()}`
                          )}`}{" "}
                        {/* {year} */}
                      </Typography>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {updatedRoomTypeData?.map((group) => (
              <Fragment key={group.room_type_name}>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      background: "#FFFFFF",
                      border: "0.5px solid #FCF0D4",
                      cursor: "pointer",
                      p: 0,
                    }}
                    onClick={() => toggleExpand(group.room_type_name)}
                  >
                    <Box sx={{ display: "flex", alignItems: "stretch" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: "14.149vw", sm: "14.03vw", md: "8.194vw" },
                          padding: {
                            xs: "4.5% 2%",
                            sm: "11.2% 3%",
                            md: "11.5% 3.5%",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            ...getGradientTextStyle(palette),
                            fontSize: {
                              xs: "2.565vw",
                              sm: "1.679vw",
                              md: "0.973vw",
                            },
                            fontWeight: 600,
                          }}
                        >
                          {group.room_type_name}
                        </Typography>
                        {expandedRoomTypes[group.room_type_name] ? (
                          <ArrowDropUp sx={{ color: "#AAAAAA" }} />
                        ) : (
                          <ArrowDropDown sx={{ color: "#AAAAAA" }} />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: "13vw", sm: "8vw", md: "6vw" },
                          borderLeft: "0.5px solid #FCF0D4",
                        }}
                      >
                        <Typography
                          sx={{
                            ...getGradientTextStyle(palette),
                            fontSize: {
                              xs: "2.565vw",
                              sm: "1.679vw",
                              md: "0.973vw",
                            },
                            fontWeight: 700,
                          }}
                        >
                          {getRoomTypeCounts(group, selectedDate).vacant}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {daysOfMonths.map((day, index) => (
                    <TableCell
                      key={`${group.room_type_key}-count-${index}`}
                      align="center"
                      sx={{
                        border: "0.5px solid #FCF0D4",
                        backgroundColor: "#FFFDF7",
                        cursor: "pointer",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                      onClick={() => toggleExpand(group.room_type_name)}
                    >
                      {renderCountsCell(
                        countsMap[`${group.room_type_key}_${index}`]
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {expandedRoomTypes[group.room_type_name] &&
                  group.rooms.map((room) => (
                    <TableRow
                      key={room.room_id}
                      sx={{ minHeight: { md: "4.167vw" } }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          background: "white",
                          border: "0.5px solid #FCF0D4",
                          p: 0,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "stretch" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              columnGap: "1vw",
                              justifyContent: "center",
                              width: {
                                xs: "14.149vw",
                                sm: "14.03vw",
                                md: "8.194vw",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#515151",
                                fontSize: {
                                  xs: "2.565vw",
                                  sm: "1.679vw",
                                  md: room.room_no ? "0.973vw" : "0.834vw",
                                },
                                fontWeight: 500,
                              }}
                              align="center"
                            >
                              {room.room_no ||
                                t(
                                  "pages.home_page.room_view_section.unassigned"
                                )}
                            </Typography>

                            {!!room.room_no ? (
                              <Box>
                                <Box
                                  sx={{
                                    height: {
                                      xs: "5.642vw",
                                      sm: "3.598vw",
                                      md: "2.084vw",
                                    },
                                    width: {
                                      xs: "5.642vw",
                                      sm: "3.598vw",
                                      md: "2.084vw",
                                    },
                                  }}
                                >
                                  <img
                                    alt=""
                                    src={
                                      room.is_under_maintenance
                                        ? IconConstants.Calendarblocked
                                        : houseKeepingIcons[
                                            room.cleaning_status_code
                                          ]
                                    }
                                    loading="lazy"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              ""
                            )}
                          </Box>
                          <Box
                            sx={{
                              width: { xs: "13vw", sm: "8vw", md: "6vw" },
                              borderLeft: "0.5px solid #FCF0D4",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {!!room.room_no && (
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: "2.565vw",
                                    sm: "1.679vw",
                                    md: "0.973vw",
                                  },
                                  fontWeight: 600,
                                  color: isRoomVacantOnDate(room, selectedDate)
                                    ? AVAILABILITY_COLORS.vacant
                                    : "#B0B0B0",
                                }}
                              >
                                {isRoomVacantOnDate(room, selectedDate)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {daysOfMonths.map((day, index) => {
                        const date = addDays(new Date(startOfPrevMonth), index);

                        const bookingInfo = getCellStatus(
                          room.room_id,
                          date,
                          new Date(startOfPrevMonth),
                          new Date(endOfNextMonth)
                        );
                        const selectedDate = date.setHours(0, 0, 0, 0);
                        const isPastDate =
                          selectedDate < new Date().setHours(0, 0, 0, 0);

                        const getMaxCheckoutDate = (bookingInfo) => {
                          return bookingInfo.reduce((maxDate, booking) => {
                            const checkoutDate = new Date(
                              booking.fake_checkout || booking.checkout
                            );
                            return checkoutDate > maxDate
                              ? checkoutDate
                              : maxDate;
                          }, new Date(0));
                        };

                        const getMinCheckInDate = (bookingInfo) => {
                          return bookingInfo.reduce((minDate, booking) => {
                            const checkInDate = new Date(booking.checkin);
                            return checkInDate < minDate
                              ? checkInDate
                              : minDate;
                          }, new Date(0));
                        };

                        const minCheckinDate = getMinCheckInDate(bookingInfo);
                        const maxCheckoutDate = getMaxCheckoutDate(bookingInfo);

                        // Compare with maintenance_until date
                        const maintenanceUntilDate = room.is_under_maintenance
                          ? getLocalTimeFromUtc(room.maintenance_until)
                              ?.dateString
                          : null;

                        const maintenanceStartDate = room.is_under_maintenance
                          ? getLocalTimeFromUtc(room.maintenance_start)
                              ?.dateString
                          : null;

                        const fakeBlockedDate = maintenanceUntilDate
                          ? new Date(
                              Math.max(
                                new Date(maintenanceUntilDate),
                                maxCheckoutDate
                              )
                            )
                          : maxCheckoutDate;

                        const fakeBlockedStartDate = maintenanceStartDate
                          ? new Date(
                              Math.min(
                                new Date(maintenanceStartDate),
                                minCheckinDate
                              )
                            )
                          : minCheckinDate;

                        const isOriginalBlockedRange =
                          maintenanceStartDate &&
                          maintenanceUntilDate &&
                          format(selectedDate, "yyyy-MM-dd") >=
                            format(maintenanceStartDate, "yyyy-MM-dd") &&
                          format(selectedDate, "yyyy-MM-dd") <=
                            format(maintenanceUntilDate, "yyyy-MM-dd");

                        const isOriginalBlockedEndDate =
                          maintenanceUntilDate &&
                          format(selectedDate, "yyyy-MM-dd") ===
                            format(maintenanceUntilDate, "yyyy-MM-dd");

                        const isBlocked =
                          room.is_under_maintenance &&
                          format(selectedDate, "yyyy-MM-dd") <=
                            format(fakeBlockedDate, "yyyy-MM-dd") &&
                          format(selectedDate, "yyyy-MM-dd") >=
                            format(fakeBlockedStartDate, "yyyy-MM-dd");

                        const isClickable =
                          bookingInfo.length === 0 &&
                          !isPastDate &&
                          !!room.room_no &&
                          !isBlocked &&
                          room?.occupancy_status_code != CHECK_IN;
                        return (
                          <TableCell
                            key={index}
                            sx={{
                              border: "0.5px solid #FCF0D4",
                              position: "relative",
                              padding: 0,
                              verticalAlign: "top",
                              cursor: isClickable ? "pointer" : "default",
                            }}
                            onClick={() =>
                              isClickable &&
                              handleCellClick({
                                room_key: room.room_key,
                                booking_status: room.booking_status,
                                selectedDate,
                              })
                            }
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                minHeight: {
                                  xs: "15.385vw",
                                  sm: "7.195vw",
                                  md: "4.167vw",
                                },
                              }}
                            >
                              {isBlocked ? (
                                <Box
                                  sx={getBlockedBarStyle(
                                    isOriginalBlockedRange
                                  )}
                                >
                                  <Typography
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      ml: "5%",
                                      fontSize: {
                                        xs: "2.31vw",
                                        sm: "1.439vw",
                                        md: "0.834vw",
                                      },
                                      fontWeight: 500,
                                      width: {
                                        xs: "15.051vw",
                                        sm: "9.832vw",
                                        md: "5.694vw",
                                      },
                                    }}
                                  >
                                    {format(selectedDate, "yyyy-MM-dd") ===
                                    format(maintenanceStartDate, "yyyy-MM-dd")
                                      ? t(
                                          "pages.home_page.room_view_section.blocked"
                                        )
                                      : ""}
                                  </Typography>
                                  {isOriginalBlockedEndDate && (
                                    <CustomTooltip
                                      placement="top"
                                      arrow
                                      sx={{
                                        "& .MuiTooltip-tooltip": {
                                          backgroundColor: "#FFFFFF",
                                          width: "fit-content",
                                          minWidth: "10vw",
                                          minHeight: "3vw",
                                          padding: "4%",
                                          borderRadius: "8px",
                                        },
                                      }}
                                      PopperProps={{
                                        modifiers: [
                                          {
                                            name: "preventOverflow",
                                            options: {
                                              boundary: "window",
                                            },
                                          },
                                        ],
                                      }}
                                      title={
                                        <>
                                          <Typography
                                            sx={{
                                              fontSize: {
                                                xs: "2.564vw",
                                                sm: "1.439vw",
                                                md: "0.834vw",
                                              },
                                              color: "#474747",
                                            }}
                                          >
                                            {room.maintenance_reason}
                                          </Typography>
                                        </>
                                      }
                                      open={activeTooltip === room.room_id}
                                    >
                                      <Info
                                        sx={{
                                          fontSize: {
                                            xs: "3.077vw",
                                            sm: "1.919vw",
                                            md: "1.112vw",
                                          },
                                          mr: "5%",
                                          color: "#666666",
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          handleTooltipToggle(room.room_id)
                                        }
                                        onMouseEnter={() =>
                                          handleTooltipToggle(room.room_id)
                                        }
                                        onMouseLeave={handleTooltipClose}
                                      />
                                    </CustomTooltip>
                                  )}
                                </Box>
                              ) : (
                                ""
                              )}
                              {bookingInfo?.map((booking, bookingIndex) => {
                                const {
                                  fake_checkout,
                                  checkout,
                                  status,
                                  reservation_key,
                                  booking_id,
                                } = booking;

                                let isRealCheckout = true;
                                if (
                                  !!booking.fake_checkout &&
                                  checkout !== fake_checkout &&
                                  new Date(checkout) <= new Date(date)
                                ) {
                                  isRealCheckout = false;
                                }

                                return (
                                  <Box
                                    key={bookingIndex}
                                    sx={getEventBarStyle({
                                      status,
                                      reservation_key,
                                      booking_id,
                                      isRealCheckout,
                                    })}
                                    onClick={(event) =>
                                      isRealCheckout &&
                                      handleStatusBarClick(event, {
                                        ...booking,
                                        room_type_name: room?.room_type_name,
                                        is_under_maintenance:
                                          room.is_under_maintenance,
                                      })
                                    }
                                    ref={(el) =>
                                      (barRefs.current[booking.booking_id] = el)
                                    }
                                  >
                                    {booking.isFirstVisibleCell &&
                                      isRealCheckout && (
                                        <Typography
                                          sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            ml: "5%",
                                            fontSize: {
                                              xs: "2.31vw",
                                              sm: "1.439vw",
                                              md: "0.834vw",
                                            },
                                            fontWeight: 500,
                                            width: {
                                              xs: "15.051vw",
                                              sm: "9.832vw",
                                              md: "5.694vw",
                                            },
                                          }}
                                        >
                                          {booking.customerName}
                                        </Typography>
                                      )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        id={id}
        open={openpop}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "4px",
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "40vw", sm: "23vw", md: "15.2vw" },
            padding: "7% 4%",
          }}
        >
          {optionsData
            .filter((option) => can(option.permFeature, option.permAction))
            .filter((option) => {
              const isReservedStatus = [CONFIRMED, TENTATIVE].includes(
                activeRowData?.booking_status
              );

              const isCheckInPastDate =
                activeRowData?.checkin &&
                new Date(activeRowData?.checkin).setHours(0, 0, 0, 0) <
                  new Date().setHours(0, 0, 0, 0);

              const isCheckInToday =
                activeRowData?.checkin &&
                new Date(activeRowData?.checkin).toDateString() ===
                  new Date().toDateString();

              const hideForRoomNo = !activeRowData?.room_no
                ? ["Room Shift"].includes(option.label)
                : ["Assign Room"].includes(option.label);

              if (isReservedStatus) {
                if (isCheckInToday) {
                  if (activeRowData?.is_under_maintenance) {
                    return (
                      !["Check In", "Check Out"].includes(option.label) &&
                      !hideForRoomNo
                    );
                  } else {
                    return (
                      !["Check Out"].includes(option.label) && !hideForRoomNo
                    );
                  }
                } else if (isCheckInPastDate) {
                  return ["Guest Details", "Cancel Reservation"].includes(
                    option.label
                  );
                } else {
                  return (
                    !["Check In", "Check Out"].includes(option.label) &&
                    !hideForRoomNo
                  );
                }
              } else {
                return ![
                  "Check In",
                  "Cancel Reservation",
                  "Assign Room",
                ].includes(option.label);
              }
            })
            .map((val, ind) => (
              <Button
                key={ind}
                fullWidth
                sx={{
                  paddingY: "4%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textTransform: "none",
                  textWrap: "nowrap",
                }}
                onClick={() => {
                  val.action(ind);
                }}
                endIcon={
                  <Box
                    sx={{
                      width: { xs: "5.129vw", sm: "2.398vw", md: "1.389vw" },
                      height: { xs: "5.129vw", sm: "2.398vw", md: "1.389vw" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <img
                      src={val.icon}
                      alt=""
                      style={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                }
              >
                <Typography
                  sx={{
                    fontSize: { xs: "2.821vw", sm: "1.679vw", md: "1.111vw" },
                    color: "#5A5A5A",
                  }}
                >
                  {t(
                    `pages.home_page.room_view_section.${val.label
                      .toLowerCase()
                      .replace(/ /g, "_")}`
                  )}
                </Typography>
              </Button>
            ))}
        </Box>
      </Popover>

      <Modal open={openModal} onClose={handleCloseModal} closeAfterTransition>
        <Fade in={openModal}>
          <Box
            sx={{
              minWidth: modalContent === "extendStay" && {
                xs: "60%",
                md: "35%",
              },
              borderRadius: "16px",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              padding: "1.5%",
              zIndex: 20,
            }}
          >
            {modalContent === "roomShift" && (
              <RoomShift
                bookingData={activeRowData}
                handleCloseModal={handleCloseModal}
                handleClosePopover={handleClosePopover}
                resetData={getAllBookings}
              />
            )}
            {modalContent === "extendStay" && (
              <ExtendStay
                bookingData={activeRowData}
                handleCloseModal={handleCloseModal}
                handleClosePopover={handleClosePopover}
                resetData={getAllBookings}
              />
            )}

            {modalContent === "cancelReservation" && (
              <CancelReservationModal
                bookingData={{
                  ...activeRowData,
                  room_data: [
                    {
                      expected_checkin_date: activeRowData.checkin_date,
                      expected_checkout_date: activeRowData.checkout_date,
                      room_type_name: activeRowData.room_type_name,
                    },
                  ],
                }}
                handleCloseModal={handleCloseModal}
                handleClosePopover={handleClosePopover}
                reset={getAllBookings}
              />
            )}
          </Box>
        </Fade>
      </Modal>
      <AssignRoomModal
        data={activeRowData}
        assignRoomAnchorEl={assignRoomAnchorEl}
        setAssignRoomAnchorEl={setAssignRoomAnchorEl}
        handleClosePopover={handleClosePopover}
        reset={getAllBookings}
      />
    </>
  );
};

export default WeekView;
